import React, { useState } from "react";
import { Row, Col, Alert } from "reactstrap";
import { useWizard } from "react-use-wizard";
import FormInput from "../../../../../../toolkit/formInput";
import WizardFormStep from "../../components/WizardFormStep";
import WizardFooter from "../../../../../../toolkit/wizardFooter";
import * as yup from "yup";
import withStyles from "isomorphic-style-loader/withStyles";
import orgStyles from "../../organizationSetup/styles.scss";
import s from "./styles.scss";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import { useLoading } from "../../../../../../hooks/useLoading";
import { MmsService } from "../../../../../../services/mmsService";
import { AlertColor } from "@mui/material";
import { saveMmsSetupData } from "../../../../../../utils/firebase/mmsSetup";
import { syncMmsSenderAreaCode } from "../../../../../../utils/mmsOnboardingApi";
import { formatMmsApiErrorMessage } from "../../../../../../utils/mmsApiErrorMessage";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export const portNumberCheckSchema = yup.object({
  phoneNumber: yup
    .string()
    .required("Phone number is required")
    .matches(
      /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
      "Please enter a valid phone number"
    ),
});

interface PortNumberCheckProps {
  charityId?: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  onConfigurationSaved?: () => void | Promise<void>;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
}

const PortNumberCheck: React.FC<PortNumberCheckProps> = ({
  charityId,
  config,
  serviceAccessToken,
  onConfigurationSaved,
  showSnackbar,
}) => {
  const { loading, runWithLoading } = useLoading();
  const { previousStep } = useWizard();
  const [portabilityChecked, setPortabilityChecked] = useState(false);
  const [isPortable, setIsPortable] = useState(false);
  const [portabilityDetails, setPortabilityDetails] = useState<{
    estimatedDays?: number;
    portType?: string;
    losingCarrier?: string;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleNext = async (formData: any) => {
    const phoneNumber = formData.phoneNumber.replace(/\D/g, '');

    setErrorMessage(null);
    setPortabilityChecked(false);
    setIsPortable(false);
    setPortabilityDetails(null);

    await runWithLoading(async () => {
      try {
        if (!config || !serviceAccessToken || !charityId) {
          throw new Error("Missing configuration. Please try again.");
        }

        const response = await MmsService.checkPortability(
          config,
          phoneNumber,
          charityId,
          serviceAccessToken
        );

        if (response.portable) {
          if (charityId) {
            await saveMmsSetupData(charityId, {
              mmsSenderAreaCode: phoneNumber,
              phoneNumberMethod: 'portNumber',
              status: 'pending',
              configuredAt: Date.now(),
              estimatedDays: response.estimatedDays,
              losingCarrier: response.losingCarrier,
              portType: response.portType,
            });
            await syncMmsSenderAreaCode(
              config.adminServiceUrl!,
              charityId,
              serviceAccessToken,
              phoneNumber
            );
          }

          setIsPortable(true);
          setPortabilityChecked(true);
          setPortabilityDetails({
            estimatedDays: response.estimatedDays,
            portType: response.portType,
            losingCarrier: response.losingCarrier,
          });
          setErrorMessage(null);

          if (onConfigurationSaved) {
            await onConfigurationSaved();
          } else if (showSnackbar) {
            const daysText = response.estimatedDays
              ? ` Transfer typically takes ${response.estimatedDays} business days.`
              : '';
            showSnackbar(`Great! Your number can be ported.${daysText}`, "success");
          }
        } else {
          setPortabilityChecked(true);
          setIsPortable(false);
          const message = response.message || "This number cannot be ported. Try getting a new number with an area code instead.";
          setErrorMessage(message);
          throw new Error(message);
        }
      } catch (error) {
        setIsPortable(false);
        setPortabilityChecked(true);
        const errorMsg = formatMmsApiErrorMessage(
          error,
          "Failed to check phone portability. Please try again."
        );
        setErrorMessage(errorMsg);
        throw new Error(errorMsg);
      }
    }, 1000);
  };

  return (
    <WizardFormStep
      title="Enter Your Phone Number"
      subtitle="We'll check if it can be transferred to our service"
    >
      <Row className="mt-4 justify-content-center">
        <Col md="8">
          <FormInput
            name="phoneNumber"
            type="tel"
            icon="fa-solid fa-mobile"
            placeholder="Phone Number (e.g., (555) 123-4567)"
          />

          <p className={orgStyles.helperText}>
            We'll automatically handle the transfer process for you.
          </p>

          {portabilityChecked && isPortable && (
            <Alert color="success" fade={false} className={s.successAlert}>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <CheckCircleIcon className="me-2" style={{ flexShrink: 0, marginTop: "2px" }} />
                <div>
                  <div>
                    <strong>Great! Your number can be ported.</strong>
                  </div>
                  {portabilityDetails?.estimatedDays && (
                    <div className="mt-1">
                      Estimated time: {portabilityDetails.estimatedDays} business days
                    </div>
                  )}
                  {portabilityDetails?.losingCarrier && (
                    <div className="mt-1 text-muted" style={{ fontSize: "13px" }}>
                      Current carrier: {portabilityDetails.losingCarrier}
                    </div>
                  )}
                </div>
              </div>
            </Alert>
          )}

          {errorMessage && (
            <Alert color="danger" fade={false} className={s.dangerAlert}>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <ErrorIcon className="me-2" style={{ flexShrink: 0, marginTop: "2px" }} />
                <div>
                  <div>
                    <strong>Not Portable:</strong> {errorMessage}
                  </div>
                  <div className="mt-2">
                    You can try a different number, or{" "}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        previousStep();
                      }}
                      style={{ color: "inherit", textDecoration: "underline", fontWeight: 500 }}
                    >
                      go back to get a new number instead
                    </a>
                  </div>
                </div>
              </div>
            </Alert>
          )}
        </Col>
      </Row>

      <WizardFooter
        schema={portNumberCheckSchema}
        onFinish={handleNext}
        finishButtonText="Check Portability"
        loading={loading}
        loadingText="Checking portability..."
      />
    </WizardFormStep>
  );
};

export default withStyles(s, orgStyles)(PortNumberCheck);
